/**
 * DadosLeadBlock — sidebar de dados editaveis do lead (PATCH).
 *
 * Edicao em bloco: estado local espelha o lead; "Salvar alterações" envia o
 * diff via `onSalvar(campos)`. Mantem a tela simples e idempotente (KISS) em
 * vez de N saves inline por campo. Campos: descricao, empresa/cargo,
 * temperatura/status (select), tags (chips), contato principal, contatos
 * extras (tel/email secundario, instagram, site), enderecos titulados,
 * data_nascimento, valor estimado, origem, bio e observacoes.
 *
 * So o que a pessoa TOCOU entra no PATCH (CRM-06/FORM-07): o telefone
 * carrega como foi gravado, a mascara roda no blur e nunca gera diff sozinha
 * — antes, "+55 11 ..." abria o bloco ja "alterado" e salvar a Temperatura
 * regravava o numero cortado.
 *
 * Recarga do lead por outro bloco (nota, tarefa, parceiro — CRM-26): com
 * edicao pendente, o que foi tocado fica, o resto acompanha o servidor e um
 * aviso diz que o lead mudou. Trocou de lead ou foi o proprio salvar: vale
 * o servidor.
 *
 * Enderecos (ADR-0071): EnderecosEditor com a lista do lead. O diff so leva
 * `enderecos` (a lista completa) quando ela mudou — chave ausente = o backend
 * nao mexe. Os cartoes re-hidratam quando os enderecos mudam no servidor.
 *
 * Contato depois da conversao (CRM-11): com `lead.cliente_resumo`, nome,
 * telefone e e-mail sao os da ficha do cliente — aparecem somente leitura
 * com o botao "Editar na ficha do cliente" (`onAbrirCliente`). O backend
 * recusa PATCH desses campos com 400 quando o lead tem cliente.
 *
 * Quando `podeEditar=false`, renderiza tudo read-only (sem inputs).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiPlus, FiX } from "react-icons/fi";

import Block from "../../../components/Block";
import AsyncButton from "../../../components/AsyncButton";
import EnderecosEditor from "../../../components/EnderecosEditor";
import { listarRepresentantes } from "../../../services/comercial";
import {
  ORIGEM_LEAD_LABEL,
  ORIGENS_LEAD,
  STATUS_LEAD,
  TEMPERATURAS,
  origemEscolhivel,
  sincronizarParceirosLead,
} from "../../../services/crm";
import { buscarCep } from "../../../services/enderecos";
import {
  Field,
  FieldLabel,
  FieldRow,
  FieldValue,
  Input,
  Select,
  Textarea,
} from "../../Orcamentos/styles";
import { formatarDinheiro, parseDinheiro } from "../../../utils/dinheiro";
import { mascaraDocumento, normalizarDocumento } from "../../../utils/documento";
import {
  capitalizarNome,
  mascaraTelefone,
  moedaParaNumero,
  numeroParaMoeda,
} from "../../../utils/leadFormat";

import {
  enderecosAlterados,
  enderecosDoLead,
  errosDeEnderecoDaResposta,
  prepararEnderecos,
  validarEnderecosDoLead,
} from "../enderecosLead";
import { InlineError, InlineHint } from "../styles";
import {
  InlineField,
  InlineLabel,
  TagChip,
  TagsWrap,
} from "./styles";

const MSG_CONFIRA_ENDERECOS = "Confira os endereços destacados.";
const MSG_DESCRICAO_OBRIGATORIA = "A descrição é obrigatória.";
const MSG_VALOR_NEGATIVO = "Valor estimado não pode ser negativo.";
const MSG_LEAD_RECARREGADO =
  "O lead foi atualizado; suas alterações ainda não foram salvas.";
const MSG_CONTATO_DO_CLIENTE =
  "Nome, telefone, e-mail e CPF/CNPJ agora são os da ficha do cliente.";
// CRM-15: o CPF/CNPJ e contato legal — com cliente vinculado, vive na ficha.
const CAMPOS_DE_CONTATO = ["contato_telefone", "contato_email", "documento"];

// Campos string simples editaveis (label + key). `noBlur` normaliza ao sair
// do campo (ADR-0059) — nunca a cada tecla.
const CAMPOS_TEXTO = [
  { key: "empresa", label: "Empresa", placeholder: "Nome da empresa" },
  { key: "cargo", label: "Cargo", placeholder: "Cargo do contato" },
  { key: "contato_telefone", label: "Telefone", placeholder: "(00) 00000-0000", noBlur: mascaraTelefone },
  { key: "contato_email", label: "E-mail", placeholder: "contato@email.com" },
  { key: "documento", label: "CPF/CNPJ", placeholder: "000.000.000-00", noBlur: mascaraDocumento },
  { key: "telefone_secundario", label: "Telefone 2", placeholder: "Telefone secundário", noBlur: mascaraTelefone },
  { key: "email_secundario", label: "E-mail 2", placeholder: "E-mail secundário" },
  { key: "instagram", label: "Instagram", placeholder: "@perfil" },
  { key: "site", label: "Site", placeholder: "https://" },
];

const CAMPOS_EDITAVEIS = [
  "titulo", "empresa", "cargo", "contato_nome", "contato_sobrenome",
  "contato_telefone", "contato_email", "documento", "telefone_secundario", "email_secundario",
  "instagram", "site", "data_nascimento", "descricao_bio", "observacoes",
  "temperatura", "status", "origem", "origem_outro", "valor_estimado",
  // CRM-24: o follow-up so era gravado pelo Registrar contato e nunca podia
  // ser alterado nem limpo — a pendencia ficava eterna.
  "proxima_acao", "data_proxima_acao",
];

function valoresIniciais(lead) {
  const base = {};
  CAMPOS_EDITAVEIS.forEach((k) => {
    const v = lead[k];
    base[k] = v == null ? "" : String(v);
  });
  // Dinheiro hidrata formatado; telefone entra COMO FOI GRAVADO — cortar ou
  // mascarar aqui era o que fabricava diff sem ninguem tocar no campo.
  base.valor_estimado = numeroParaMoeda(lead.valor_estimado);
  return base;
}

const textoOriginal = (lead, k) => (lead[k] == null ? "" : String(lead[k]));

/** Valor que vai no PATCH para o campo tocado, ou `undefined` se e igual ao gravado. */
function valorParaPatch(k, v, lead) {
  if (k === "valor_estimado") {
    const novo = moedaParaNumero(v);
    const orig = lead.valor_estimado == null ? null : Number(lead.valor_estimado);
    return novo !== orig ? novo : undefined;
  }
  // Documento e gravado so com o numero; a mascara do blur nao e mudanca.
  if (k === "documento") {
    const numero = normalizarDocumento(v);
    return numero !== normalizarDocumento(textoOriginal(lead, k)) ? numero : undefined;
  }
  const texto = k === "contato_telefone" || k === "telefone_secundario" ? v.trim() : v;
  if (texto === textoOriginal(lead, k)) return undefined;
  if (k === "data_nascimento" || k === "data_proxima_acao") return texto === "" ? null : texto;
  return texto;
}

/** Contato somente leitura, vindo do cliente vinculado (CRM-11). */
function ContatoDoCliente({ cliente, onAbrirCliente }) {
  return (
    <InlineField role="group" aria-labelledby="lead-contato-cliente">
      <InlineLabel id="lead-contato-cliente">Contato (ficha do cliente)</InlineLabel>
      <FieldRow>
        <FieldLabel>Nome</FieldLabel>
        <FieldValue>{cliente.nome || "—"}</FieldValue>
      </FieldRow>
      <FieldRow>
        <FieldLabel>Telefone</FieldLabel>
        <FieldValue>{cliente.telefone || "—"}</FieldValue>
      </FieldRow>
      <FieldRow>
        <FieldLabel>E-mail</FieldLabel>
        <FieldValue>{cliente.email || "—"}</FieldValue>
      </FieldRow>
      <FieldRow>
        <FieldLabel>CPF/CNPJ</FieldLabel>
        <FieldValue>
          {cliente.documento ? mascaraDocumento(cliente.documento, cliente.tipo) : "—"}
        </FieldValue>
      </FieldRow>
      <InlineHint>{MSG_CONTATO_DO_CLIENTE}</InlineHint>
      {onAbrirCliente && (
        <button
          type="button"
          className="btn btn--ghost"
          onClick={onAbrirCliente}
          style={{ justifyContent: "flex-start", marginTop: 4 }}
        >
          Editar na ficha do cliente
        </button>
      )}
    </InlineField>
  );
}

function DadosLeadBlock({
  lead,
  onSalvar,
  podeEditar,
  request,
  leadId,
  onParceirosChange,
  onAbrirCliente,
}) {
  const clienteVinculado = lead.cliente_resumo?.id ? lead.cliente_resumo : null;
  const [valores, setValores] = useState(() => valoresIniciais(lead));
  // Campos que a pessoa alterou nesta edicao: so eles entram no PATCH.
  const [tocados, setTocados] = useState(() => new Set());
  const [tags, setTags] = useState(() =>
    Array.isArray(lead.tags) ? lead.tags : []
  );
  const [tagsTocadas, setTagsTocadas] = useState(false);
  const [novaTag, setNovaTag] = useState("");
  const [erroValor, setErroValor] = useState("");
  const [leadRecarregado, setLeadRecarregado] = useState(false);

  // ----- Enderecos titulados (ADR-0071) -----
  const [enderecos, setEnderecos] = useState(() => enderecosDoLead(lead));
  const [errosEnderecos, setErrosEnderecos] = useState([]);
  const buscarEndereco = useCallback((cep) => buscarCep(request, cep), [request]);

  // Depois de uma tentativa com erro, revalida a cada alteracao: o aviso some
  // quando a pessoa corrige e acompanha o cartao quando ela reordena ou remove.
  const alterarEnderecos = (lista) => {
    setEnderecos(lista);
    if (errosEnderecos.length) {
      setErrosEnderecos(validarEnderecosDoLead(lista, enderecosDoLead(lead)));
    }
  };

  // ----- Parceiros indicadores (ID 26) -----
  // Aplicacao imediata: cada add/remove chama o endpoint dedicado
  // (POST /leads/{id}/parceiros com a lista COMPLETA) e recarrega o lead.
  const parceiros = useMemo(
    () => (Array.isArray(lead.parceiros) ? lead.parceiros : []),
    [lead.parceiros]
  );
  const [repsDisponiveis, setRepsDisponiveis] = useState([]);
  const [salvandoParceiros, setSalvandoParceiros] = useState(false);

  useEffect(() => {
    if (!podeEditar || !request) return undefined;
    let ativo = true;
    listarRepresentantes(request, { pageSize: 200 }, { silentError: true })
      .then((pagina) => {
        if (!ativo) return;
        const lista = Array.isArray(pagina) ? pagina : pagina?.results || [];
        setRepsDisponiveis(lista.filter((r) => r?.ativo !== false));
      })
      .catch(() => ativo && setRepsDisponiveis([]));
    return () => {
      ativo = false;
    };
  }, [podeEditar, request]);

  const idsParceirosAtuais = useMemo(
    () => parceiros.map((p) => p.representante_id).filter(Boolean),
    [parceiros]
  );

  const opcoesParceiro = useMemo(
    () =>
      repsDisponiveis.filter(
        (r) => !idsParceirosAtuais.some((id) => String(id) === String(r.id))
      ),
    [repsDisponiveis, idsParceirosAtuais]
  );

  const sincronizarParceiros = useCallback(
    async (novosIds) => {
      if (!request || !leadId) return;
      setSalvandoParceiros(true);
      try {
        await sincronizarParceirosLead(request, leadId, novosIds);
        if (onParceirosChange) await onParceirosChange();
      } catch {
        /* erro tratado pelo auto-toast do request */
      } finally {
        setSalvandoParceiros(false);
      }
    },
    [request, leadId, onParceirosChange]
  );

  const adicionarParceiro = (repId) => {
    if (repId) sincronizarParceiros([...idsParceirosAtuais, repId]);
  };

  const removerParceiro = (repId) =>
    sincronizarParceiros(
      idsParceirosAtuais.filter((id) => String(id) !== String(repId))
    );

  // Lead da recarga anterior: diz se os enderecos mudaram NO SERVIDOR.
  const leadAnteriorRef = useRef(lead);
  // O proprio salvar recarrega o lead: ai vale o servidor, sem aviso.
  const salvandoRef = useRef(false);
  // Espelhos para o efeito de recarga ler sem entrar nas dependencias.
  const pendentesRef = useRef({ tocados, tagsTocadas, enderecos });
  pendentesRef.current = { tocados, tagsTocadas, enderecos };

  const descartarRascunho = (novoLead) => {
    setValores(valoresIniciais(novoLead));
    setTocados(new Set());
    setTags(Array.isArray(novoLead.tags) ? novoLead.tags : []);
    setTagsTocadas(false);
    setErroValor("");
    setLeadRecarregado(false);
  };

  // Re-sincroniza quando o lead recarrega (apos salvar em outro bloco).
  useEffect(() => {
    const anterior = leadAnteriorRef.current;
    leadAnteriorRef.current = lead;
    const pendentes = pendentesRef.current;
    const trocouDeLead = anterior.id !== lead.id;
    const temRascunho =
      pendentes.tocados.size > 0
      || pendentes.tagsTocadas
      || enderecosAlterados(pendentes.enderecos, anterior.enderecos);
    if (trocouDeLead || salvandoRef.current || !temRascunho) {
      descartarRascunho(lead);
    } else {
      // Nota, tarefa ou parceiro recarregou o lead no meio da edicao: o que
      // foi tocado fica, o resto acompanha o servidor (CRM-26).
      setValores((prev) => {
        const base = valoresIniciais(lead);
        pendentes.tocados.forEach((k) => {
          base[k] = prev[k];
        });
        return base;
      });
      if (!pendentes.tagsTocadas) setTags(Array.isArray(lead.tags) ? lead.tags : []);
      setLeadRecarregado(true);
    }
    // Com os enderecos do servidor iguais, o rascunho dos cartoes fica —
    // senao a pessoa perdia o endereco que estava digitando. Mudou no
    // servidor (o proprio salvar, outra pessoa): vale o servidor, com ids novos.
    const servidorMudou =
      trocouDeLead || enderecosAlterados(enderecosDoLead(anterior), lead.enderecos);
    if (servidorMudou) {
      setEnderecos(enderecosDoLead(lead));
      setErrosEnderecos([]);
    }
  }, [lead]);

  const setCampo = (campo, valor) => {
    setValores((prev) => ({ ...prev, [campo]: valor }));
    setTocados((prev) => (prev.has(campo) ? prev : new Set(prev).add(campo)));
  };

  // Tira o lead do sino: sem data nao ha follow-up a cobrar.
  const limparFollowUp = () => {
    setCampo("proxima_acao", "");
    setCampo("data_proxima_acao", "");
  };
  const temFollowUp = Boolean(valores.proxima_acao || valores.data_proxima_acao);

  // Normalizacao no blur: formata o que esta no campo sem marcar como tocado
  // — a mascara nao e edicao da pessoa e nao pode gerar diff (CRM-06).
  const normalizarNoBlur = (campo, fn) =>
    setValores((prev) => ({ ...prev, [campo]: fn(prev[campo]) }));

  const adicionarTag = () => {
    const t = novaTag.trim();
    if (!t || tags.includes(t)) {
      setNovaTag("");
      return;
    }
    setTags((prev) => [...prev, t]);
    setTagsTocadas(true);
    setNovaTag("");
  };

  const removerTag = (t) => {
    setTags((prev) => prev.filter((x) => x !== t));
    setTagsTocadas(true);
  };

  // Monta o diff: so os campos TOCADOS que diferem do lead original.
  const diff = useMemo(() => {
    const out = {};
    tocados.forEach((k) => {
      const v = valorParaPatch(k, valores[k], lead);
      if (v !== undefined) out[k] = v;
    });
    const tagsOriginais = Array.isArray(lead.tags) ? lead.tags : [];
    const mudouTags =
      tags.length !== tagsOriginais.length ||
      tags.some((t, i) => t !== tagsOriginais[i]);
    if (mudouTags) out.tags = tags;
    // Lista completa so quando mudou: chave ausente = o backend nao mexe.
    if (enderecosAlterados(enderecos, lead.enderecos)) {
      out.enderecos = prepararEnderecos(enderecos).enderecos;
    }
    return out;
  }, [valores, tocados, tags, enderecos, lead]);

  // Quando a origem deixa de ser OUTRO, o texto livre nao faz mais sentido —
  // limpa origem_outro no payload (o backend so exibe quando origem==OUTRO).
  const payload = useMemo(() => {
    const out = { ...diff };
    if ("origem" in out && out.origem !== "OUTRO" && lead.origem_outro) {
      out.origem_outro = "";
    }
    return out;
  }, [diff, lead.origem_outro]);

  const temMudancas = Object.keys(payload).length > 0;
  // Descricao vazia derrubava o PATCH inteiro com "titulo: em branco" —
  // bloqueia antes, com o aviso no campo (CRM-39).
  const erroDescricao = valores.titulo.trim() ? "" : MSG_DESCRICAO_OBRIGATORIA;
  const podeSalvar = temMudancas && !erroDescricao && !erroValor;

  const validarValorNoBlur = () => {
    const n = parseDinheiro(valores.valor_estimado);
    if (n !== null && n < 0) {
      setErroValor(MSG_VALOR_NEGATIVO);
      return;
    }
    setErroValor("");
    normalizarNoBlur("valor_estimado", () => (n === null ? "" : formatarDinheiro(n)));
  };

  // Erro e lancado (e engolido pelo AsyncButton silentError) para nao sair o
  // toast de sucesso: o aviso fica em cada cartao e ao lado do botao.
  const salvar = async () => {
    if (!podeSalvar) return;
    if (payload.enderecos) {
      const erros = validarEnderecosDoLead(enderecos, enderecosDoLead(lead));
      setErrosEnderecos(erros);
      if (erros.length) throw new Error(MSG_CONFIRA_ENDERECOS);
    }
    salvandoRef.current = true;
    try {
      await onSalvar(payload);
      setTocados(new Set());
      setTagsTocadas(false);
      setLeadRecarregado(false);
    } catch (err) {
      const errosDaApi = errosDeEnderecoDaResposta(err, enderecos);
      if (errosDaApi) setErrosEnderecos(errosDaApi);
      throw err;
    } finally {
      salvandoRef.current = false;
    }
  };

  // ---------- Read-only ----------
  if (!podeEditar) {
    return (
      <Block title="Dados do lead">
        {clienteVinculado ? (
          <ContatoDoCliente cliente={clienteVinculado} onAbrirCliente={onAbrirCliente} />
        ) : (
          lead.contato_nome && (
            <FieldRow>
              <FieldLabel>Nome</FieldLabel>
              <FieldValue>{lead.contato_nome}</FieldValue>
            </FieldRow>
          )
        )}
        {lead.contato_sobrenome && (
          <FieldRow>
            <FieldLabel>Apelido</FieldLabel>
            <FieldValue>{lead.contato_sobrenome}</FieldValue>
          </FieldRow>
        )}
        {CAMPOS_TEXTO.map((c) =>
          lead[c.key] && !(clienteVinculado && CAMPOS_DE_CONTATO.includes(c.key)) ? (
            <FieldRow key={c.key}>
              <FieldLabel>{c.label}</FieldLabel>
              <FieldValue>{lead[c.key]}</FieldValue>
            </FieldRow>
          ) : null
        )}
        {enderecosDoLead(lead).length > 0 && (
          <InlineField role="group" aria-labelledby="lead-enderecos-leitura">
            <InlineLabel id="lead-enderecos-leitura">Endereços</InlineLabel>
            <EnderecosEditor somenteLeitura value={enderecosDoLead(lead)} />
          </InlineField>
        )}
        {Array.isArray(lead.tags) && lead.tags.length > 0 && (
          <FieldRow>
            <FieldLabel>Tags</FieldLabel>
            <FieldValue>
              <TagsWrap>
                {lead.tags.map((t) => (
                  <TagChip key={t}>{t}</TagChip>
                ))}
              </TagsWrap>
            </FieldValue>
          </FieldRow>
        )}
        {parceiros.length > 0 && (
          <FieldRow>
            <FieldLabel>Parceiros</FieldLabel>
            <FieldValue>
              <TagsWrap>
                {parceiros.map((p) => (
                  <TagChip key={p.id}>
                    {p.representante_nome}
                    {p.comissao_percentual ? ` · ${p.comissao_percentual}%` : ""}
                  </TagChip>
                ))}
              </TagsWrap>
            </FieldValue>
          </FieldRow>
        )}
        {lead.descricao_bio && (
          <FieldRow>
            <FieldLabel>Bio (contexto do cliente)</FieldLabel>
            <FieldValue style={{ whiteSpace: "pre-wrap" }}>
              {lead.descricao_bio}
            </FieldValue>
          </FieldRow>
        )}
        {lead.observacoes && (
          <FieldRow>
            <FieldLabel>Observações / mensagem do cliente</FieldLabel>
            <FieldValue style={{ whiteSpace: "pre-wrap" }}>
              {lead.observacoes}
            </FieldValue>
          </FieldRow>
        )}
      </Block>
    );
  }

  // ---------- Editavel ----------
  return (
    <Block title="Dados do lead">
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {leadRecarregado && temMudancas && (
          <InlineHint role="status">{MSG_LEAD_RECARREGADO}</InlineHint>
        )}

        <Field>
          Descrição *
          <Textarea
            value={valores.titulo}
            onChange={(e) => setCampo("titulo", e.target.value)}
            rows={2}
            maxLength={200}
            placeholder="Resumo curto do lead"
            aria-invalid={Boolean(erroDescricao)}
            aria-describedby={erroDescricao ? "lead-titulo-erro" : undefined}
          />
        </Field>
        {erroDescricao && (
          <InlineError id="lead-titulo-erro">{erroDescricao}</InlineError>
        )}

        {/* minWidth 0 nas duplas lado a lado: sem ele o campo nao encolhe
            abaixo do input e estoura a coluna estreita do cockpit. */}
        <div style={{ display: "flex", gap: 8 }}>
          <Field style={{ flex: 1, minWidth: 0 }}>
            Temperatura
            <Select
              value={valores.temperatura}
              onChange={(e) => setCampo("temperatura", e.target.value)}
            >
              <option value="">— Sem definir —</option>
              {TEMPERATURAS.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field style={{ flex: 1, minWidth: 0 }}>
            Status
            <Select
              value={valores.status}
              onChange={(e) => setCampo("status", e.target.value)}
            >
              <option value="">— Sem definir —</option>
              {STATUS_LEAD.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
          <Field style={{ flex: 2, minWidth: 0 }}>
            Próxima ação
            <Input
              type="text"
              value={valores.proxima_acao}
              onChange={(e) => setCampo("proxima_acao", e.target.value)}
              placeholder="Ex.: Ligar para confirmar a medição"
              maxLength={200}
            />
          </Field>
          <Field style={{ flex: 1, minWidth: 0 }}>
            Data
            <Input
              type="date"
              value={valores.data_proxima_acao}
              onChange={(e) => setCampo("data_proxima_acao", e.target.value)}
            />
          </Field>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={limparFollowUp}
            disabled={!temFollowUp}
            title="Sem próxima ação o lead sai do sino e da Agenda"
          >
            Limpar
          </button>
        </div>

        <InlineField>
          <InlineLabel>Tags</InlineLabel>
          <TagsWrap>
            {tags.map((t) => (
              <TagChip key={t}>
                {t}
                <button
                  type="button"
                  onClick={() => removerTag(t)}
                  aria-label={`Remover tag ${t}`}
                >
                  <FiX size={11} aria-hidden="true" />
                </button>
              </TagChip>
            ))}
          </TagsWrap>
          <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
            <Input
              type="text"
              value={novaTag}
              onChange={(e) => setNovaTag(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  adicionarTag();
                }
              }}
              placeholder="Nova tag + Enter"
              maxLength={40}
              style={{ flex: 1 }}
            />
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              onClick={adicionarTag}
            >
              <FiPlus size={13} aria-hidden="true" />
            </button>
          </div>
        </InlineField>

        <InlineField>
          <InlineLabel>Parceiros indicadores</InlineLabel>
          {parceiros.length > 0 && (
            <TagsWrap>
              {parceiros.map((p) => (
                <TagChip key={p.id}>
                  {p.representante_nome}
                  {p.comissao_percentual ? ` · ${p.comissao_percentual}%` : ""}
                  {p.representante_ativo === false && (
                    <span
                      className="badge neutral no-dot"
                      style={{ marginLeft: 4, fontSize: 10 }}
                      title="Parceiro desativado em Parceiros; a comissão dele continua no lead até você remover"
                    >
                      Inativo
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => removerParceiro(p.representante_id)}
                    aria-label={`Remover ${p.representante_nome}`}
                    disabled={salvandoParceiros}
                  >
                    <FiX size={11} aria-hidden="true" />
                  </button>
                </TagChip>
              ))}
            </TagsWrap>
          )}
          <Select
            value=""
            onChange={(e) => {
              if (e.target.value) adicionarParceiro(e.target.value);
            }}
            disabled={salvandoParceiros || opcoesParceiro.length === 0}
            style={{ marginTop: 4 }}
          >
            <option value="">
              {repsDisponiveis.length === 0
                ? "— Nenhum parceiro cadastrado —"
                : "+ Adicionar parceiro…"}
            </option>
            {opcoesParceiro.map((r) => (
              <option key={r.id} value={r.id}>
                {r.nome}
                {r.comissao_percentual ? ` (RT ${r.comissao_percentual}%)` : ""}
              </option>
            ))}
          </Select>
          <div
            style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 4 }}
          >
            A soma das comissões vira o RT do orçamento gerado deste lead.
          </div>
        </InlineField>

        {clienteVinculado && (
          <ContatoDoCliente cliente={clienteVinculado} onAbrirCliente={onAbrirCliente} />
        )}

        <div style={{ display: "flex", gap: 8 }}>
          {!clienteVinculado && (
            <Field style={{ flex: 1, minWidth: 0 }}>
              Nome completo
              <Input
                type="text"
                value={valores.contato_nome}
                onChange={(e) => setCampo("contato_nome", e.target.value)}
                onBlur={() => normalizarNoBlur("contato_nome", capitalizarNome)}
                placeholder="Nome completo da pessoa"
              />
            </Field>
          )}
          <Field style={{ flex: 1, minWidth: 0 }}>
            Apelido
            <Input
              type="text"
              value={valores.contato_sobrenome}
              onChange={(e) => setCampo("contato_sobrenome", e.target.value)}
              onBlur={() => normalizarNoBlur("contato_sobrenome", capitalizarNome)}
              placeholder="Como prefere ser chamado"
            />
          </Field>
        </div>

        {CAMPOS_TEXTO.filter(
          (c) => !(clienteVinculado && CAMPOS_DE_CONTATO.includes(c.key))
        ).map((c) => (
          <Field key={c.key}>
            {c.label}
            <Input
              type="text"
              value={valores[c.key]}
              onChange={(e) => setCampo(c.key, e.target.value)}
              onBlur={c.noBlur ? () => normalizarNoBlur(c.key, c.noBlur) : undefined}
              placeholder={c.placeholder}
            />
          </Field>
        ))}

        <InlineField role="group" aria-labelledby="lead-enderecos-rotulo">
          <InlineLabel id="lead-enderecos-rotulo">Endereços</InlineLabel>
          <EnderecosEditor
            value={enderecos}
            onChange={alterarEnderecos}
            buscar={request ? buscarEndereco : undefined}
            erros={errosEnderecos}
            idPrefix="lead-enderecos"
          />
        </InlineField>

        <Field>
          Nascimento
          <Input
            type="date"
            value={valores.data_nascimento}
            onChange={(e) => setCampo("data_nascimento", e.target.value)}
          />
        </Field>

        <Field>
          Valor
          <Input
            type="text"
            inputMode="numeric"
            value={valores.valor_estimado}
            onChange={(e) => {
              setCampo("valor_estimado", e.target.value);
              if (erroValor) setErroValor("");
            }}
            // D-6A: formata no blur, nao a cada tecla. Negativo fica como
            // esta, com o aviso (CRM-39).
            onBlur={validarValorNoBlur}
            placeholder="R$ 0,00"
            aria-invalid={Boolean(erroValor)}
            aria-describedby={erroValor ? "lead-valor-erro" : undefined}
          />
        </Field>
        {erroValor && <InlineError id="lead-valor-erro">{erroValor}</InlineError>}

        <Field>
          Origem
          <Select
            value={valores.origem}
            onChange={(e) => setCampo("origem", e.target.value)}
          >
            {/* Origem legada (LOJA/EVENTO) ou vazia de lead antigo: aparece
                selecionada com o rotulo, mas nao se escolhe — o backend
                recusa as duas com 400 (CRM-30). */}
            {!origemEscolhivel(valores.origem) && (
              <option value={valores.origem} disabled>
                {ORIGEM_LEAD_LABEL[valores.origem] || "— Sem definir —"}
              </option>
            )}
            {ORIGENS_LEAD.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
          {valores.origem === "OUTRO" && (
            <Input
              type="text"
              value={valores.origem_outro}
              onChange={(e) => setCampo("origem_outro", e.target.value)}
              placeholder="Qual a origem?"
              maxLength={120}
              style={{ marginTop: 6 }}
            />
          )}
        </Field>

        <Field>
          Bio (contexto do cliente)
          <Textarea
            value={valores.descricao_bio}
            onChange={(e) => setCampo("descricao_bio", e.target.value)}
            rows={3}
            placeholder="Quem é o cliente, o que busca, contexto da obra"
          />
        </Field>

        <Field>
          Observações / mensagem do cliente
          <Textarea
            value={valores.observacoes}
            onChange={(e) => setCampo("observacoes", e.target.value)}
            rows={3}
            placeholder="O que o cliente contou ao entrar (site, WhatsApp, Novo Lead)"
          />
        </Field>
        <InlineHint>
          Anotações da equipe ficam em Notas gerais; comentário é sobre um
          registro da timeline.
        </InlineHint>

        {errosEnderecos.length > 0 && (
          <InlineError role="alert">{MSG_CONFIRA_ENDERECOS}</InlineError>
        )}

        <AsyncButton
          variant="primary"
          onClick={salvar}
          successMessage="Dados atualizados."
          silentError
          disabled={!podeSalvar}
          style={{ alignSelf: "flex-start" }}
        >
          Salvar alteracoes
        </AsyncButton>
      </div>
    </Block>
  );
}

export default DadosLeadBlock;
